import type {
  Notification,
  NotificationPage,
} from "@circle-react/types/Notification";
import type { QueryClient } from "react-query";

import {
  getFetchNotificationQueryKey,
  NOTIFICATIONS_PER_PAGE,
} from "./constants";
import type { Filter } from "./NotificationContext";
import { TABS } from "./NotificationContext";

interface NotificationQueryData {
  pageParams: unknown[];
  pages: NotificationPage[];
}

export interface PrependNewNotificationParams {
  activeFilter: Filter;
  isNotificationsTabV2Enabled: boolean;
  notification: Notification;
  queryClient: QueryClient;
}

const prependToQueryCache = (
  queryClient: QueryClient,
  queryKey: (string | undefined)[],
  notification: Notification,
) => {
  const oldData = queryClient.getQueryData<NotificationQueryData>(queryKey);

  if (!oldData?.pages?.length) {
    return;
  }

  const firstPage = oldData.pages[0];

  const existingIds = new Set(firstPage.records.map(n => n.id));
  if (existingIds.has(notification.id)) {
    return;
  }

  const updatedRecords = [notification, ...firstPage.records].slice(
    0,
    NOTIFICATIONS_PER_PAGE,
  );

  const lastNotification = updatedRecords.at(-1);
  const updatedNextSearchAfter = lastNotification
    ? String(lastNotification.id)
    : firstPage.next_search_after;

  const updatedFirstPage: NotificationPage = {
    ...firstPage,
    records: updatedRecords,
    total_count: firstPage.total_count + 1,
    has_next_page: true,
    next_search_after: updatedNextSearchAfter,
  };

  queryClient.setQueryData<NotificationQueryData>(queryKey, {
    ...oldData,
    pages: [updatedFirstPage],
    pageParams: [oldData.pageParams[0]],
  });
};

export const prependNewNotificationToInboxTab = ({
  activeFilter,
  notification,
  queryClient,
  isNotificationsTabV2Enabled,
}: PrependNewNotificationParams) => {
  const filter = isNotificationsTabV2Enabled ? activeFilter : undefined;

  if (
    isNotificationsTabV2Enabled &&
    notification.notification_group === TABS.MENTIONS
  ) {
    const inboxKey = getFetchNotificationQueryKey(TABS.INBOX, filter);
    const mentionsKey = getFetchNotificationQueryKey(TABS.MENTIONS, filter);
    prependToQueryCache(queryClient, inboxKey, notification);
    prependToQueryCache(queryClient, mentionsKey, notification);
    return;
  }

  const notificationGroup = isNotificationsTabV2Enabled
    ? notification.notification_group
    : TABS.INBOX;
  const queryKey = getFetchNotificationQueryKey(notificationGroup, filter);
  prependToQueryCache(queryClient, queryKey, notification);
};
